import React, { useReducer, useCallback, useEffect } from 'react';
import { analytics } from '@sendgrid/bloodhound';
import ERRORTYPES from '../constants/errorTypes';
import { AccountInputsEvents } from '../analytics/accountInputsEvents';
import { trackOnce } from '../utils/Analytics';
import createRequest from '../utils/requestService';
import {
  validateEmail,
  validateUsername,
  validatePassword,
} from '../utils/formInput';
import errorTypes from '../constants/errorTypes';

export interface AccountInputs {
  onToggleUseEmailAddress: () => void;
  onChangeEmail: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlurEmail: () => void;
  onChangeUsername: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlurUsername: () => void;
  onChangePassword: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlurPassword: () => void;
  onPasswordError: (error: string) => void;
  accountInputsState: AccountInputsState;
}

const trackUsernameComplete = trackOnce();
const trackPasswordComplete = trackOnce();
const trackEmailComplete = trackOnce();

const validateUniqueUsername = async (
  username: string,
  isEmail: boolean
): Promise<InputValidation> => {
  return fetch(
    createRequest(`/v3/public/signup/username/${username}`, {
      method: 'GET'
    })
  )
    .then(response => {
      switch (response.status) {
        case 404:
          isEmail
            ? trackEmailComplete(AccountInputsEvents.emailAddressCompleted())
            : trackUsernameComplete(AccountInputsEvents.usernameCompleted());
          return {
            isValid: true,
            info: isEmail
              ? ERRORTYPES.NO_TYPE_SET
              : ERRORTYPES.USERNAME_AVAILABLE
          };
        case 429:
          return {
            isValid: false,
            info: ERRORTYPES.USERNAME_TOO_MANY_REQUESTS
          };
        case 204:
          return {
            isValid: false,
            info: isEmail
              ? ERRORTYPES.EMAIL_USERNAME_TAKEN
              : ERRORTYPES.USERNAME_TAKEN
          };
        case 400:
          return {
            isValid: false,
            info: isEmail
              ? ERRORTYPES.EMAIL_INVALID
              : ERRORTYPES.USERNAME_INVALID
          };
        default:
          return {
            isValid: false,
            info: isEmail
              ? ERRORTYPES.EMAIL_DEFAULT
              : ERRORTYPES.USERNAME_DEFAULT
          };
      }
    })
    .catch(() => {
      return {
        isValid: true,
        info: errorTypes.NO_TYPE_SET
      };
    });
};

const useAccountInputs = (): AccountInputs => {

  const [accountInputsState, dispatch] = useReducer<
    React.Reducer<AccountInputsState, AccountInputsAction>
  >(reducer, defaultInitialState);

  // Prevents the case where the form is invalidated due to a user typing in an email address that 
  // is already used as a username, and then immediateley clicking to uncheck "Use email address as username"
  // without blurring first on the email address input
  useEffect(() => {
    if (accountInputsState.emailInfo === errorTypes.EMAIL_USERNAME_TAKEN && !accountInputsState.useEmailAddress) {
      return dispatch({
        type: ActionType.UpdateEmailValidation,
        payload: {
          isValid: true,
          info: errorTypes.NO_TYPE_SET
        }
      });
    }
  }, [accountInputsState.useEmailAddress, accountInputsState.emailInfo])

  const onToggleUseEmailAddress = useCallback(() => {
    analytics.track(AccountInputsEvents.useEmailAddressToggled);
    // If we are toggling from using email address as username to not using email address as username 
    // and there is a email username taken error,
    // we want to remove the error
    if (
      accountInputsState.useEmailAddress &&
      accountInputsState.emailInfo === ERRORTYPES.EMAIL_USERNAME_TAKEN
    ) {
      if (accountInputsState.emailIsValid) {
        trackEmailComplete(AccountInputsEvents.emailAddressCompleted());
      }
      dispatch({
        type: ActionType.ToggleUseEmailAddress,
        payload: {
          isValid: true,
          info: ERRORTYPES.NO_TYPE_SET
        }
      });
    // If we are toggling from not using email address as username to using email address as username
    // and the email is a valid,
    // we want to validate if the email is a unique username
    } else if (
      !accountInputsState.useEmailAddress &&
      !accountInputsState.emailInfo
    ) {
      validateUniqueUsername(accountInputsState.emailValue, true).then(
        ({ isValid, info }) => {
          dispatch({
            type: ActionType.ToggleUseEmailAddress,
            payload: {
              isValid,
              info
            }
          });
        }
      );
    } else {
      dispatch({
        type: ActionType.ToggleUseEmailAddress,
        payload: {
          isValid: accountInputsState.emailIsValid,
          info: accountInputsState.emailInfo
        }
      });
    }
  }, [
    analytics,
    AccountInputsEvents,
    accountInputsState,
    dispatch,
    validateUniqueUsername
  ]);

  const onChangeEmail = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      dispatch({
        type: ActionType.ChangeEmail,
        payload: event.currentTarget.value
      });
    },
    [dispatch]
  );

  const onBlurEmail = useCallback(() => {
    if (accountInputsState.emailIsValid && accountInputsState.emailValue) {
      if (accountInputsState.useEmailAddress) {
        validateUniqueUsername(accountInputsState.emailValue, true).then(
          ({ isValid, info }) => {
            dispatch({
              type: ActionType.UpdateEmailValidation,
              payload: {
                isValid,
                info
              }
            });
          }
        );
      } else {
        trackEmailComplete(AccountInputsEvents.emailAddressCompleted());
      }
    }
  }, [accountInputsState, validateUniqueUsername, dispatch]);

  const onChangeUsername = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      dispatch({
        type: ActionType.ChangeUsername,
        payload: event.currentTarget.value
      });
    },
    [dispatch]
  );

  const onBlurUsername = useCallback(() => {
    if (
      accountInputsState.usernameIsValid &&
      accountInputsState.usernameValue
    ) {
      validateUniqueUsername(accountInputsState.usernameValue, false).then(
        ({ isValid, info }) => {
          dispatch({
            type: ActionType.UpdateUsernameValidation,
            payload: {
              isValid,
              info
            }
          });
        }
      );
    }
  }, [accountInputsState, dispatch, validateUniqueUsername]);

  const onChangePassword = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      dispatch({
        type: ActionType.ChangePassword,
        payload: event.currentTarget.value
      });
    },
    [dispatch]
  );

  const onPasswordError = (error: string) => {
    dispatch({
      type: ActionType.CreateUserFailurePassword,
      payload: error
    })
  }

  const onBlurPassword = () => {
    if (
      accountInputsState.passwordIsValid &&
      accountInputsState.passwordValue
    ) {
      trackPasswordComplete(AccountInputsEvents.passwordCompleted());
    }
  };

  return {
    onToggleUseEmailAddress,
    onChangeEmail,
    onBlurEmail,
    onChangeUsername,
    onBlurUsername,
    onChangePassword,
    onBlurPassword,
    onPasswordError,
    accountInputsState
  };
};

const reducer: React.Reducer<AccountInputsState, AccountInputsAction> = (
  state,
  action
) => {
  switch (action.type) {
    case ActionType.ToggleUseEmailAddress: {
      let { useEmailAddress } = state;
      const { isValid, info } = action.payload as InputValidation;
      return {
        ...state,
        useEmailAddress: !useEmailAddress,
        emailIsValid: isValid,
        emailInfo: info
      };
    }
    case ActionType.ChangeEmail: {
      const emailValue = action.payload as string;
      const { isValid, errorType } = validateEmail(emailValue);
      return {
        ...state,
        emailValue,
        emailInfo: errorType,
        emailIsValid: isValid
      };
    }
    case ActionType.UpdateEmailValidation: {
      const { isValid, info } = action.payload as InputValidation;
      return {
        ...state,
        emailInfo: info,
        emailIsValid: isValid
      };
    }
    case ActionType.ChangeUsername: {
      const usernameValue = action.payload as string;
      const {
        isValid: usernameIsValid,
        errorType: usernameInfo
      } = validateUsername(usernameValue, true);
      let { passwordInfo } = state;
      return {
        ...state,
        usernameValue,
        usernameInfo,
        usernameIsValid,
        passwordInfo
      };
    }
    case ActionType.UpdateUsernameValidation: {
      const { isValid, info } = action.payload as InputValidation;
      return {
        ...state,
        usernameInfo: info,
        usernameIsValid: isValid
      };
    }
    case ActionType.ChangePassword: {
      const passwordValue = action.payload as string;
      const { isValid, errorType } = validatePassword(
        passwordValue, '', '', true
      );
      return {
        ...state,
        passwordValue,
        passwordInfo: errorType,
        passwordIsValid: isValid
      };
    }
    case ActionType.CreateUserFailurePassword: {
      const error = action.payload as string;
      return {
        ...state,
        passwordInfo: error,
        passwordIsValid: false
      };
    }
    default:
      return state;
  }
};


const defaultInitialState: AccountInputsState = {
  useEmailAddress: true,
  emailValue: '',
  emailInfo: '',
  emailIsValid: true,
  usernameValue: '',
  usernameInfo: '',
  usernameIsValid: true,
  passwordValue: '',
  passwordInfo: '',
  passwordIsValid: true
};

export interface AccountInputsState {
  useEmailAddress: boolean;
  emailValue: string;
  emailInfo: string;
  emailIsValid: boolean;
  usernameValue: string;
  usernameInfo: string;
  usernameIsValid: boolean;
  passwordValue: string;
  passwordInfo: string;
  passwordIsValid: boolean;
}

export enum ActionType {
  ToggleUseEmailAddress = 'ToggleUseEmailAddress',
  ChangeEmail = 'ChangeEmail',
  UpdateEmailValidation = 'UpdateEmailValidation',
  ChangeUsername = 'ChangeUsername',
  UpdateUsernameValidation = 'UpdateUsernameValidation',
  ChangePassword = 'ChangePassword',
  CreateUserFailurePassword = 'CreateUserFailurePassword',
}

interface InputValidation {
  isValid: boolean;
  info: string;
}

interface AccountInputsAction {
  type: ActionType;
  payload?: string | null | InputValidation;
}

export default useAccountInputs;